/**
 * PreloadManager Component
 * Manages texture preloading inside the Canvas
 */

import { useState, useEffect, useMemo, useRef } from 'react'
import lodConfig from './earth/config/lodConfig.json'
import { usePreloadAllLODs } from './earth/hooks/usePreloadAllLODs'
import { usePreloadAllLODs as useMoonPreloadAllLODs } from './moon/hooks/usePreloadAllLODs'
import { getDeviceInfo } from '../utils/deviceDetection'

export function PreloadManager({ children, onProgress }) {
  // Detect device and orientation (memoized)
  const deviceInfo = useMemo(() => getDeviceInfo(), [])
  const isMobile = deviceInfo.isMobile
  
  // Get appropriate LOD config for device and filter out disabled LODs (memoized)
  const lodLevels = useMemo(() => {
    const allLods = isMobile ? lodConfig.mobile.lodLevels : lodConfig.desktop.lodLevels
    return allLods.filter(lod => lod.enabled !== false) // Default to enabled if not specified
  }, [isMobile])

  // Preload Earth LOD levels.
  // Mobile drops normal + specular maps (barely visible at phone screen size /
  // zoom) to cut ~40% of Earth tile GPU memory. The TSL material applies those
  // maps only `if (array)`, so omitting them is safe — no shader change needed.
  const lodNamesToPreload = useMemo(() => lodLevels.map(lod => lod.name), [lodLevels])
  const earthTextureTypes = useMemo(
    () => isMobile
      ? ['diffuse', 'nightmap', 'clouds']
      : ['diffuse', 'normal', 'specular', 'nightmap', 'clouds'],
    [isMobile]
  )
  const { isPreloading: earthIsPreloading, preloadedCount: earthPreloadedCount, currentLoadingItem: earthLoadingItem } = usePreloadAllLODs(lodNamesToPreload, true, earthTextureTypes)

  // Preload Moon LOD levels
  const { isPreloading: moonIsPreloading, preloadedCount: moonPreloadedCount, currentLoadingItem: moonLoadingItem } = useMoonPreloadAllLODs(lodNamesToPreload, true)

  // Calculate total textures to preload (Earth type count is device-dependent;
  // Moon has 1 texture type per LOD)
  const totalEarthTextures = lodNamesToPreload.length * earthTextureTypes.length
  const totalMoonTextures = lodNamesToPreload.length
  const totalTextures = totalEarthTextures + totalMoonTextures
  const totalPreloaded = earthPreloadedCount + moonPreloadedCount
  const isPreloading = earthIsPreloading || moonIsPreloading
  
  // Determine what's currently loading
  const currentLoadingPhase = isPreloading ? 'LOADING TEXTURES' : 'LOADING MODELS'

  // Calculate progress percentage and notify parent
  const prevProgressRef = useRef({ progress: 0, complete: false, phase: '' })
  
  useEffect(() => {
    const progress = totalTextures > 0 ? (totalPreloaded / totalTextures) * 100 : 0
    const complete = !isPreloading && totalPreloaded === totalTextures
    
    // Only call onProgress if values actually changed
    const prev = prevProgressRef.current
    if (prev.progress !== progress || prev.complete !== complete || prev.phase !== currentLoadingPhase) {
      prevProgressRef.current = { progress, complete, phase: currentLoadingPhase }
      if (onProgress) {
        onProgress(progress, complete, currentLoadingPhase)
      }
    }
  }, [totalPreloaded, totalTextures, isPreloading, currentLoadingPhase, onProgress])

  // Don't render children until preloading is complete
  if (isPreloading) {
    return null
  }

  return children
}

export default PreloadManager
